export type BookingHotelSummary = {
  componentId?: string;
  name: string;
  room: string;
  city: string;
  checkInDate: string;
  checkOutDate: string;
  voucherUrl?: string;
};

export type BookingFlightSummary = {
  componentId?: string;
  airline: string;
  flightNumber: string;
  from: string;
  to: string;
  departureTime: string;
  arrivalTime: string;
  travelClass: string;
  stops: string;
  voucherUrl?: string;
};

export type BookingTransferSummary = {
  componentId?: string;
  serviceType: string;
  vehicle: string;
  route: string;
  transferDate: string;
  voucherUrl?: string;
};

export type BookingActivitySummary = {
  componentId?: string;
  title: string;
  city: string;
  serviceDate: string;
  voucherUrl?: string;
};

export type BookingVisaSummary = {
  componentId?: string;
  title: string;
  country: string;
  voucherUrl?: string;
};

export type BookingInsuranceSummary = {
  componentId?: string;
  title: string;
  coverageStartDate: string;
  coverageEndDate: string;
  voucherUrl?: string;
};

export type BookingPlan = {
  version: 1;
  kind: 'package';
  summary?: {
    title?: string | null;
    travelStartDate?: string | null;
    travelEndDate?: string | null;
    totalAmount?: number;
    currency?: string;
  };
  components?: {
    hotels?: Array<{
      componentId?: string;
      title?: string | null;
      city?: string | null;
      checkInDate?: string | null;
      checkOutDate?: string | null;
      rooms?: Array<{
        roomType?: string | null;
        occupancyLabel?: string | null;
      }>;
    }>;
    flights?: Array<{
      componentId?: string;
      title?: string | null;
      journeys?: Array<{
        from?: string | null;
        to?: string | null;
        departureAt?: string | null;
        arrivalAt?: string | null;
        airline?: string | null;
        flightNumber?: string | null;
        cabinClass?: string | null;
      }>;
    }>;
    transfers?: Array<{
      componentId?: string;
      title?: string | null;
      transferDate?: string | null;
      from?: string | null;
      to?: string | null;
      vehicle?: string | null;
      transferType?: string | null;
    }>;
    activities?: Array<{
      componentId?: string;
      title?: string | null;
      city?: string | null;
      serviceDate?: string | null;
    }>;
    visas?: Array<{
      componentId?: string;
      title?: string | null;
      country?: string | null;
    }>;
    insurance?: Array<{
      componentId?: string;
      title?: string | null;
      coverageStartDate?: string | null;
      coverageEndDate?: string | null;
    }>;
  };
};

export type BookingComponentsSummary = {
  travelStartDate: string;
  travelEndDate: string;
  totalAmount: number;
  hotels: BookingHotelSummary[];
  flights: BookingFlightSummary[];
  transfers: BookingTransferSummary[];
  activities: BookingActivitySummary[];
  visas: BookingVisaSummary[];
  insurance: BookingInsuranceSummary[];
  cabLine: string;
};

type SummaryInput = {
  amount_paise?: unknown;
  amountPaise?: unknown;
  booking_plan?: BookingPlan | null;
  bookingPlan?: BookingPlan | null;
  start_at?: unknown;
  startAt?: unknown;
  travelStartDate?: unknown;
  end_at?: unknown;
  endAt?: unknown;
  travelEndDate?: unknown;
  totalAmount?: unknown;
};

const toText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const toNumber = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

export const formatMoney = (amount: number) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

export const normalizeDate = (value: unknown) => {
  if (!value) return '';
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

export const formatDisplayDate = (value: string) => {
  const normalized = normalizeDate(value);
  if (!normalized) return '—';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDisplayDateTime = (value: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const mergeItems = <T,>(
  items: T[],
  isSameItem: (left: T, right: T) => boolean,
  mergeItem: (existing: T, incoming: T) => T,
) => items.reduce<T[]>((acc, item) => {
  const existingIndex = acc.findIndex((existing) => isSameItem(existing, item));
  if (existingIndex === -1) {
    acc.push(item);
    return acc;
  }
  acc[existingIndex] = mergeItem(acc[existingIndex], item);
  return acc;
}, []);

export const buildBookingComponentsSummaryFromPlan = (plan: BookingPlan | null | undefined): BookingComponentsSummary | null => {
  if (!plan || !plan.components) return null;

  const getVoucherUrl = (value: any) => toText(
    value?.voucherUrl
      || value?.voucher_url
      || value?.pdfUrl
      || value?.pdf_url,
  );

  const hotels = (Array.isArray(plan.components.hotels) ? plan.components.hotels : []).map((hotel) => ({
    componentId: toText(hotel?.componentId),
    name: toText(hotel?.title),
    room: toText(hotel?.rooms?.[0]?.roomType),
    city: toText(hotel?.city),
    checkInDate: normalizeDate(hotel?.checkInDate),
    checkOutDate: normalizeDate(hotel?.checkOutDate),
    voucherUrl: getVoucherUrl(hotel),
  })).filter((hotel) => hotel.name);

  const flights = (Array.isArray(plan.components.flights) ? plan.components.flights : []).flatMap((flight) => {
    const journeys = Array.isArray(flight?.journeys) && flight.journeys.length > 0
      ? flight.journeys
      : [{
        from: '',
        to: '',
        departureAt: '',
        arrivalAt: '',
        airline: '',
        flightNumber: '',
        cabinClass: '',
      }];
    return journeys.map((journey) => ({
      componentId: toText(flight?.componentId),
      airline: toText(journey?.airline),
      flightNumber: toText(journey?.flightNumber),
      from: toText(journey?.from),
      to: toText(journey?.to),
      departureTime: toText(journey?.departureAt),
      arrivalTime: toText(journey?.arrivalAt),
      travelClass: toText(journey?.cabinClass),
      stops: '—',
      voucherUrl: getVoucherUrl(flight),
    })).filter((journey) => journey.airline || journey.flightNumber || journey.from || journey.to);
  });

  const transfers = (Array.isArray(plan.components.transfers) ? plan.components.transfers : []).map((transfer) => ({
    componentId: toText(transfer?.componentId),
    serviceType: toText(transfer?.transferType || transfer?.title, 'Cab'),
    vehicle: toText(transfer?.vehicle),
    route: transfer?.from || transfer?.to ? `${toText(transfer?.from, '—')} → ${toText(transfer?.to, '—')}` : '',
    transferDate: normalizeDate(transfer?.transferDate),
    voucherUrl: getVoucherUrl(transfer),
  })).filter((transfer) => transfer.serviceType || transfer.vehicle || transfer.route || transfer.transferDate);

  const activities = (Array.isArray(plan.components.activities) ? plan.components.activities : []).map((activity) => ({
    componentId: toText(activity?.componentId),
    title: toText(activity?.title),
    city: toText(activity?.city),
    serviceDate: normalizeDate(activity?.serviceDate),
    voucherUrl: getVoucherUrl(activity),
  })).filter((activity) => activity.title);

  const visas = (Array.isArray(plan.components.visas) ? plan.components.visas : []).map((visa) => ({
    componentId: toText(visa?.componentId),
    title: toText(visa?.title),
    country: toText(visa?.country),
    voucherUrl: getVoucherUrl(visa),
  })).filter((visa) => visa.title);

  const insurance = (Array.isArray(plan.components.insurance) ? plan.components.insurance : []).map((item) => ({
    componentId: toText(item?.componentId),
    title: toText(item?.title),
    coverageStartDate: normalizeDate(item?.coverageStartDate),
    coverageEndDate: normalizeDate(item?.coverageEndDate),
    voucherUrl: getVoucherUrl(item),
  })).filter((item) => item.title);

  const cabLine = toText(
    (Array.isArray(plan.components.transfers) ? plan.components.transfers : [])[0]?.vehicle,
  );

  return {
    travelStartDate: normalizeDate(plan.summary?.travelStartDate),
    travelEndDate: normalizeDate(plan.summary?.travelEndDate),
    totalAmount: toNumber(plan.summary?.totalAmount),
    hotels,
    flights,
    transfers,
    activities,
    visas,
    insurance,
    cabLine,
  };
};

export const buildBookingComponentsSummary = (source: SummaryInput | null | undefined): BookingComponentsSummary | null => {
  if (!source) return null;

  const planSummary = buildBookingComponentsSummaryFromPlan(source.booking_plan || source.bookingPlan || null);
  if (!planSummary) return null;

  const rawAmount = source.amount_paise ?? source.amountPaise ?? source.totalAmount;
  const normalizedAmount = rawAmount !== undefined && rawAmount !== null && toNumber(rawAmount) > 100000
    ? toNumber(rawAmount) / 100
    : toNumber(rawAmount);

  return {
    ...planSummary,
    travelStartDate: planSummary.travelStartDate || normalizeDate(source.start_at || source.startAt || source.travelStartDate || ''),
    travelEndDate: planSummary.travelEndDate || normalizeDate(source.end_at || source.endAt || source.travelEndDate || ''),
    totalAmount: planSummary.totalAmount || normalizedAmount,
  };
};

export const getBookingComponentType = (booking: any): 'hotel' | 'flight' | 'transfer' | 'activity' | 'insurance' | 'visa' | 'other' => {
  const type = toText(
    booking?.product_type
      || booking?.type
      || booking?.category
      || booking?.service
      || booking?.package?.title
      || booking?.title,
    '',
  ).toLowerCase();

  if (type.includes('hotel')) return 'hotel';
  if (type.includes('flight')) return 'flight';
  if (type.includes('transfer') || type.includes('cab')) return 'transfer';
  if (type.includes('activity') || type.includes('tour')) return 'activity';
  if (type.includes('insurance')) return 'insurance';
  if (type.includes('visa')) return 'visa';
  return 'other';
};

export const buildBookingComponentsSummaryFromChildren = (children: any[]): BookingComponentsSummary | null => {
  if (!Array.isArray(children) || children.length === 0) return null;

  const hotelItems: BookingHotelSummary[] = [];
  const flightItems: BookingFlightSummary[] = [];
  const transferItems: BookingTransferSummary[] = [];

  children.forEach((child) => {
    const type = getBookingComponentType(child);
    const componentSummary = buildBookingComponentsSummary({
      bookingPlan: (child as any)?.booking_plan || (child as any)?.bookingPlan,
      travelStartDate: child?.travel_start_date,
      travelEndDate: child?.travel_end_date,
      totalAmount: child?.total_price,
    });

    if (type === 'hotel') {
      if (componentSummary?.hotels.length) {
        hotelItems.push(...componentSummary.hotels);
        return;
      }
      hotelItems.push({
        name: toText(child?.package?.title || child?.title || child?.product?.name, `Hotel #${child?.id || ''}`),
        room: toText(child?.product?.room_type || child?.product?.roomType, ''),
        city: toText(child?.product?.city || child?.product?.location, ''),
        checkInDate: normalizeDate(child?.travel_start_date),
        checkOutDate: normalizeDate(child?.travel_end_date),
      });
      return;
    }

    if (type === 'flight') {
      if (componentSummary?.flights.length) {
        flightItems.push(...componentSummary.flights);
        return;
      }
      flightItems.push({
        airline: toText(child?.provider || child?.package?.title || child?.title, ''),
        flightNumber: toText(child?.product?.flight_number || child?.product?.flightNumber, ''),
        from: toText(child?.product?.from || child?.product?.origin, ''),
        to: toText(child?.product?.to || child?.product?.destination, ''),
        departureTime: toText(child?.travel_start_date, ''),
        arrivalTime: toText(child?.travel_end_date, ''),
        travelClass: toText(child?.product?.class || child?.product?.travelClass, ''),
        stops: '—',
      });
      return;
    }

    if (type === 'transfer') {
      if (componentSummary?.transfers.length) {
        transferItems.push(...componentSummary.transfers);
        return;
      }
      transferItems.push({
        serviceType: 'Cab',
        vehicle: toText(child?.product?.vehicle || child?.product?.cabType || child?.title, ''),
        route: toText(child?.product?.route || '', ''),
        transferDate: normalizeDate(child?.travel_start_date),
      });
    }
  });

  return {
    travelStartDate: '',
    travelEndDate: '',
    totalAmount: 0,
    hotels: mergeItems(
      hotelItems,
      (left, right) => left.name === right.name && left.room === right.room && left.city === right.city && left.checkInDate === right.checkInDate && left.checkOutDate === right.checkOutDate,
      (existing) => existing,
    ),
    flights: mergeItems(
      flightItems,
      (left, right) => left.airline === right.airline && left.flightNumber === right.flightNumber && left.from === right.from && left.to === right.to && left.departureTime === right.departureTime,
      (existing) => existing,
    ),
    transfers: mergeItems(
      transferItems,
      (left, right) => left.serviceType === right.serviceType && left.vehicle === right.vehicle && left.route === right.route && left.transferDate === right.transferDate,
      (existing) => existing,
    ),
    activities: [],
    visas: [],
    insurance: [],
    cabLine: transferItems[0]?.vehicle || '',
  };
};

export const hasBookingComponentData = (summary: BookingComponentsSummary | null | undefined) => {
  if (!summary) return false;
  return summary.hotels.length > 0
    || summary.flights.length > 0
    || summary.transfers.length > 0
    || summary.activities.length > 0
    || summary.visas.length > 0
    || summary.insurance.length > 0;
};
